import React, { useContext, useState, useEffect, useCallback } from 'react';
import { Column } from 'react-table';
import { RouteComponentProps } from 'react-router-dom';
import { Card, Button } from 'react-bootstrap';
import { ResponseError } from '@nintendo/forms';
import ConfigContext from '../../contexts/ConfigContext';
import { generateActionMemoColumns, generateSubRowRenderFunction } from './ActionMemoRowHelper';
import { getActionMemos, getActionMemoCount } from '../../api/actionMemoListApi';
import AmCreateModal from '../common/modal/AmCreateModal';
import { AmCreateFormType } from '../common/modal/amCreateformTypes';
import { exportAmList } from '../common/listUtils';
import useStateWithSessionStorage from '../../hooks/useStateWithSessionStorage';
import { DEFAULT_INITIAL_STATE, MAX_COUNT } from '../../utils/Constants';
import DataGridControlled from '../common/datatables/DataGridControlled';
import { ActionMemoItem } from '../../api/actionMemoListApiTypes';

const TASK_LIST_PAGE_SIZE = 'taskListPageSize';

const TaskList = (routerProps: RouteComponentProps) => {
    const config = useContext(ConfigContext);

    const [searching, setSearching] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [responseError, setResponseError] = useState('');

    const initialPageSize = localStorage.getItem(TASK_LIST_PAGE_SIZE);

    const [initialState, setInitialState] = useStateWithSessionStorage(
        'taskListInitialState',
        JSON.stringify({
            ...DEFAULT_INITIAL_STATE,
            pageSize: initialPageSize ? Number(initialPageSize) : DEFAULT_INITIAL_STATE.pageSize,
        }),
    );

    const [initialDataSize, setInitialDataSize] = useStateWithSessionStorage(
        'taskListInitialDataSize',
        '-1',
    );

    const initialStateObj = JSON.parse(initialState);
    useEffect(() => {
        localStorage.setItem(TASK_LIST_PAGE_SIZE, initialStateObj.pageSize);
    }, [initialStateObj.pageSize]);

    const fetchData = useCallback(
        async ({
            pageSize,
            pageIndex,
            sortBy,
        }: {
            pageSize: number;
            pageIndex: number;
            sortBy: { desc: boolean; id: string }[];
        }) => {
            let ifDesc = null;
            let sortByString = null;

            if (sortBy.length > 0) {
                ifDesc = sortBy[0].desc;
                sortByString = sortBy[0].id;
            }
            setSearching(true);
            const newData = await getActionMemos(
                config,
                {
                    tasks: true,
                    expanded: false,
                },
                pageSize,
                pageIndex,
                sortByString,
                ifDesc,
            );
            setSearching(false);

            return newData || [];
        },
        [],
    );

    const fetchDataCount = useCallback(async () => {
        setSearching(true);
        const count = await getActionMemoCount(
            config,
            {
                tasks: true,
            },
            undefined,
        );
        setSearching(false);
        return count || 0;
    }, []);

    const columns = generateActionMemoColumns(routerProps, 'My Tasks', '/tasks', setResponseError);
    const renderRowSubComponent = generateSubRowRenderFunction();

    return (
        <div className="container-fluid mt-3">
            <div className="row row-page-header">
                <div className="col-12 d-flex align-items-center mb-2">
                    <h3 className="mr-auto text-weight-normal mb-0">My Tasks</h3>
                    <AmCreateModal
                        title="Create Action Memo"
                        toggleButton={
                            <Button variant="primary" className="mobile-hide">
                                <i className="fas fa-plus" /> New AM
                            </Button>
                        }
                        formType={AmCreateFormType.CREATE}
                        onSubmit={() => {}}
                        routerProps={routerProps}
                    />
                    <Button
                        variant="outline-primary"
                        className="mobile-hide ml-1"
                        disabled={Number(initialDataSize) <= 0 || searching || exporting}
                        onClick={async () => {
                            setExporting(true);
                            const data = await getActionMemos(
                                config,
                                {
                                    tasks: true,
                                    expanded: true,
                                },
                                MAX_COUNT,
                                null,
                                initialStateObj.sortBy && initialStateObj.sortBy.length > 0
                                    ? initialStateObj.sortBy[0].id
                                    : null,
                                initialStateObj.sortBy && initialStateObj.sortBy.length > 0
                                    ? initialStateObj.sortBy[0].desc
                                    : null,
                            );
                            if (data) {
                                exportAmList(data, `AM Tasks.csv`);
                            }
                            setExporting(false);
                        }}
                    >
                        {exporting ? (
                            <>
                                <i className="fas fa-circle-notch fa-spin" /> Generating CSV...
                            </>
                        ) : (
                            <>
                                <i className="fas fa-file-export" /> Export CSV
                            </>
                        )}
                    </Button>
                </div>
            </div>
            <Card>
                <Card.Body>
                    <ResponseError errorMessage={responseError} />
                    <DataGridControlled<ActionMemoItem>
                        columns={columns as Column<ActionMemoItem>[]}
                        initialState={JSON.parse(initialState)}
                        setInitialState={setInitialState}
                        fetchData={fetchData}
                        fetchDataCount={fetchDataCount}
                        searchString="{}"
                        setSearching={setSearching}
                        setResponseError={setResponseError}
                        renderRowSubComponent={renderRowSubComponent}
                        initialDataSize={Number(initialDataSize)}
                        setInitialDataSize={setInitialDataSize}
                    />
                </Card.Body>
            </Card>
        </div>
    );
};

export default TaskList;
